import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
});

/**
 * Same ceiling as `MAX_ANALYSIS_SELECTION` server-side (`CLIENT_TYPING_MAX_SELECTION`), so the
 * picker stops the user before the 400. The limit is the Groq account's tokens-per-minute
 * budget, not the page — the server sends its own `max` back with the error if the two drift.
 */
export const MAX_SELECTION = 80;
export const MIN_SELECTION = 3;

export interface ClientProfileMember {
    id: string;
    name: string;
    city?: string;
}

export interface ClientProfile {
    label: string;
    description: string;
    /** Criteria that define the profile, as written by the assistant. */
    defining: string[];
    /** Counted server-side from the profile's members — always a real share of the selection. */
    count: number;
    pct: number;
    /** Medians and dominant values computed from the members, ready to print. */
    facts: { label: string; value: string }[];
    members: ClientProfileMember[];
    /** French one-liner to run in the Database page's AI bar. */
    searchPrompt: string;
}

export interface DistributionItem {
    label: string;
    count: number;
    pct: number;
}

export interface StatBlock {
    id: string;
    label: string;
    /** How many of the selected establishments carry this field at all. */
    coverage: number;
    items: DistributionItem[];
}

export interface SelectionStats {
    total: number;
    blocks: StatBlock[];
    highlights: { label: string; value: string; coverage: number }[];
}

export interface ClientTypingResponse {
    total: number;
    stats: SelectionStats;
    summary: string;
    profiles: ClientProfile[];
    /** Establishments the assistant left out of every profile. */
    unassigned: ClientProfileMember[];
    model: string;
    /** Set when the statistics came back but the assistant did not — the page still renders. */
    aiError?: "AI_UNAVAILABLE" | "AI_CALL_FAILED" | "AI_BAD_OUTPUT" | "AI_RATE_LIMITED" | "AI_TOO_LARGE";
}

export interface EstablishmentPick {
    _id: string;
    name: string;
    city: string | null;
    postcode: string | null;
    categories: string[];
    subCategories: string[];
}

/** Picker lookup — matches on name *or* city, unlike the Database page's name-only box. */
export const searchEstablishmentsForTyping = async (q: string, limit = 10): Promise<EstablishmentPick[]> => {
    const response = await axios.get(`${API_URL}/client-profiles/search`, {
        headers: authHeaders(),
        params: { q, limit },
    });
    return response.data?.establishments ?? [];
};

export const analyzeClientTypes = async (establishmentIds: string[]): Promise<ClientTypingResponse> => {
    const response = await axios.post(
        `${API_URL}/client-profiles/analyze`,
        { establishmentIds },
        { headers: authHeaders() }
    );
    return response.data;
};


// ─── Typologies enregistrées ──────────────────────────────────────────────────

/**
 * Une typologie est la *description* d'un client, sauvegardée pour être rejouée — le pendant
 * d'un cluster, qui est une liste de clients rangés ensemble. Elle survit à l'analyse qui l'a
 * produite : le brief hebdo lui redemande « des établissements comme ça » des mois plus tard.
 */
export interface ClientTypology {
    _id: string;
    name: string;
    description?: string;
    /** la phrase que l'assistant rejoue — le seul champ dont le brief a besoin */
    searchPrompt: string;
    traits: string[];
    referenceEstablishmentIds: string[];
    origin?: string;
    createdByName?: string;
    useCount: number;
    lastUsedAt?: string | null;
    createdAt?: string;
    updatedAt?: string;
}

export interface ClientTypologyPayload {
    name: string;
    description?: string;
    searchPrompt: string;
    traits?: string[];
    referenceEstablishmentIds?: string[];
    origin?: string;
}

export const listTypologies = async (): Promise<ClientTypology[]> => {
    const response = await axios.get(`${API_URL}/client-profiles/typologies`, { headers: authHeaders() });
    return response.data?.typologies ?? [];
};

export const createTypology = async (payload: ClientTypologyPayload): Promise<ClientTypology> => {
    const response = await axios.post(`${API_URL}/client-profiles/typologies`, payload, {
        headers: authHeaders(),
    });
    return response.data;
};

export const updateTypology = async (
    id: string,
    payload: Partial<ClientTypologyPayload>
): Promise<ClientTypology> => {
    const response = await axios.patch(`${API_URL}/client-profiles/typologies/${id}`, payload, {
        headers: authHeaders(),
    });
    return response.data;
};

export const deleteTypology = async (id: string): Promise<{ ok: boolean }> => {
    const response = await axios.delete(`${API_URL}/client-profiles/typologies/${id}`, {
        headers: authHeaders(),
    });
    return response.data;
};
